import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  classroomContextId,
  withClassroomContext,
} from "@/components/workspace/navigation";
import type { Editor, TLShape } from "tldraw";
import api from "@/utils/axios-utils";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useConnectionStore } from "@/features/tutor/store/useConnectionStore";
import {
  useSpaceStore,
  type AiMode,
  type CollabMode,
  type WriteMode,
} from "../store/useSpaceStore";
import {
  AI_MODE_STUDENT_TOAST,
  COLLAB_MODE_STUDENT_TOAST,
  WRITE_MODE_STUDENT_TOAST,
} from "../constants";
import type { TldrawCameraVector } from "../spacesPresenceUtils";
import { useSpaceCtrl, type SpaceCtrlSender } from "./useSpaceCtrl";

type SpaceCamera = ReturnType<Editor["getCamera"]>;

export type StudentSpaceInfo = {
  userId: number;
  writeMode: WriteMode;
  isTeacher: boolean;
  lessonContext: string | null;
  teacherName: string | null;
  teacherId: number | null;
  aiMode: AiMode;
  collabMode: CollabMode;
  classroomIds: number[];
  lessonIds: number[];
  lessonImported: boolean;
  spaceStatus: string;
  spaceName: string;
  initialSnapshot: Record<string, unknown> | null;
  // Raw TLSync snapshot — used by the gateway DO. The frontend should read
  // personalShapes/personalCamera (already projected by the backend) instead.
  personalSnapshot: Record<string, unknown> | null;
  personalShapes: TLShape[];
  personalCamera: TldrawCameraVector | null;
  snapshotSavedAt: string | null;
};

type UseStudentSpaceSessionArgs = {
  spaceId: string | undefined;
  token: string | null;
  // Returns the live editor when available — used to snapshot the canvas at
  // close-time before the sync WS disconnects.
  getEditor: () => Editor | null;
};

export type StudentSpaceSession = {
  spaceInfo: StudentSpaceInfo | null;
  // Patch fields on the cached spaceInfo (e.g. SSE-driven spaceName updates).
  patchSpaceInfo: (patch: Partial<StudentSpaceInfo>) => void;
  joinComplete: boolean;
  // Active write-mode of the student (independent of useSpaceStore — it lives
  // here so we can react to ctrl-channel updates without store round-trips).
  writeMode: WriteMode;
  spaceClosed: boolean;
  // True when the space was already closed at join time — page renders the
  // offline ClosedCanvas, no live WS is opened.
  closedFromStart: boolean;
  // Editor snapshot captured at runtime when the space closes; passed to
  // ClosedCanvas so the student keeps seeing what was on the board.
  closedEditorSnapshot: Record<string, unknown> | null;
  spacePaused: boolean;
  // Personal-room camera restored from the last snapshot. Applied by the
  // canvas hook on mount.
  savedCamera: SpaceCamera | null;
  // Send a ctrl-channel message to the gateway.
  sendCtrl: SpaceCtrlSender;
};

/**
 * Owns the student's join lifecycle, mode/pause state, and the ctrl-channel
 * wiring that keeps that state in sync with the gateway. Pulled out of
 * StudentSpacePage so the page itself only contains layout + canvas plumbing.
 */
export function useStudentSpaceSession({
  spaceId,
  token,
  getEditor,
}: UseStudentSpaceSessionArgs): StudentSpaceSession {
  const navigate = useNavigate();
  const location = useLocation();
  const classroomId = classroomContextId(location.pathname, location.search);
  const { showNotification } = useNotificationStore();

  const [spaceInfo, setSpaceInfo] = useState<StudentSpaceInfo | null>(null);
  const [joinComplete, setJoinComplete] = useState(false);
  const [writeMode, setWriteMode] = useState<WriteMode>("read");
  const [spaceClosed, setSpaceClosed] = useState(false);
  const [closedFromStart, setClosedFromStart] = useState(false);
  const [closedEditorSnapshot, setClosedEditorSnapshot] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [savedCamera, setSavedCamera] = useState<SpaceCamera | null>(null);
  const [spacePaused, setSpacePaused] = useState(false);

  // Pause state needs to be readable synchronously from ctrl callbacks (the
  // closure captures stale state otherwise). pausedAiMode keeps the mode that
  // was active before pause so we can restore it on resume.
  const spacePausedRef = useRef(false);
  const pausedAiModeRef = useRef<AiMode>("off");

  // Single entry/exit point for "space paused" state — three places used to
  // hand-roll this: initial join, ctrl state_snapshot, ctrl paused/resumed
  // events. Factoring it out avoids the three implementations drifting.
  const enterPaused = useCallback((aiModeBeforePause: AiMode) => {
    if (spacePausedRef.current) return;
    pausedAiModeRef.current = aiModeBeforePause;
    spacePausedRef.current = true;
    setSpacePaused(true);
    useSpaceStore.getState().setAiMode("off");
  }, []);

  const exitPaused = useCallback(() => {
    if (!spacePausedRef.current) return;
    spacePausedRef.current = false;
    setSpacePaused(false);
    useSpaceStore.getState().setAiMode(pausedAiModeRef.current);
  }, []);

  // Initial join — runs once per spaceId. Failure redirects back to the list.
  useEffect(() => {
    if (!spaceId) return;
    api
      .post<StudentSpaceInfo>(`/spaces/${spaceId}/join`)
      .then((res) => {
        setSpaceInfo(res.data);
        setWriteMode(res.data.writeMode);
        useSpaceStore.getState().setAiMode(res.data.aiMode);
        useSpaceStore.getState().setCollabMode(res.data.collabMode);
        if (res.data.personalCamera) {
          setSavedCamera(res.data.personalCamera as SpaceCamera);
        }
        if (res.data.spaceStatus === "closed") {
          setSpaceClosed(true);
          setClosedFromStart(true);
          useSpaceStore.getState().setAiMode("off");
        } else if (res.data.spaceStatus === "paused") {
          enterPaused(res.data.aiMode);
        }
        setJoinComplete(true);
      })
      .catch((err) => {
        console.error("[useStudentSpaceSession] join failed:", err);
        navigate(withClassroomContext("/spaces", classroomId));
      });
  }, [spaceId, navigate, classroomId, enterPaused]);

  // Capture an editor snapshot before the sync WS disconnects so ClosedCanvas
  // keeps showing the same content the student saw a moment earlier.
  const captureEditorSnapshot = useCallback(() => {
    const snap = getEditor()?.getSnapshot() ?? null;
    setClosedEditorSnapshot(snap as Record<string, unknown> | null);
  }, [getEditor]);

  const patchSpaceInfo = useCallback((patch: Partial<StudentSpaceInfo>) => {
    setSpaceInfo((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const sendCtrl = useSpaceCtrl(
    joinComplete && !spaceClosed && !closedFromStart ? spaceId : undefined,
    token,
    {
      onConnectionChange: (isConnected) => {
        const store = useConnectionStore.getState();
        if (isConnected) store.markConnected();
        else store.markDisconnected();
      },
      onStateSnapshot: (snap) => {
        // Recover any space-level state that changed while ctrl was offline.
        // Apply unconditionally on every (re)connect — cheap and keeps us authoritative.
        if (snap.spaceStatus === "closed") {
          captureEditorSnapshot();
          setSpaceClosed(true);
          useSpaceStore.getState().setAiMode("off");
          navigate(
            withClassroomContext(
              `/spaces/${spaceId}/student-recap`,
              classroomId,
            ),
          );
          return;
        }
        if (snap.spaceStatus === "paused") {
          enterPaused(useSpaceStore.getState().aiMode);
        } else if (snap.spaceStatus === "open") {
          exitPaused();
        }
        if (
          !spacePausedRef.current &&
          useSpaceStore.getState().aiMode !== snap.aiMode
        ) {
          useSpaceStore.getState().setAiMode(snap.aiMode);
        }
        if (useSpaceStore.getState().collabMode !== snap.collabMode) {
          useSpaceStore.getState().setCollabMode(snap.collabMode);
        }
        setWriteMode((prev) =>
          prev === snap.writeMode ? prev : snap.writeMode,
        );
      },
      onWriteModeChange: (newMode) => {
        showNotification(
          WRITE_MODE_STUDENT_TOAST[newMode],
          NotificationType.SPACE_STUDENT,
        );
        setWriteMode(newMode);
      },
      onSpaceClosed: () => {
        // Snapshot before the WS disconnects (full editor state includes teacher
        // + personal shapes). Personal snapshot is saved by the DO automatically
        // — no client PATCH needed.
        captureEditorSnapshot();
        setSpaceClosed(true);
        useSpaceStore.getState().setAiMode("off");
        navigate(
          withClassroomContext(`/spaces/${spaceId}/student-recap`, classroomId),
        );
      },
      onSpacePaused: () => enterPaused(useSpaceStore.getState().aiMode),
      onSpaceResumed: () => exitPaused(),
      onCollabModeChanged: (newCollabMode) => {
        useSpaceStore.getState().setCollabMode(newCollabMode);
        showNotification(
          COLLAB_MODE_STUDENT_TOAST[newCollabMode],
          NotificationType.SPACE_STUDENT,
        );
      },
      onAiModeChanged: (newAiMode) => {
        if (spacePausedRef.current) {
          // Stash the new mode and apply it on resume — pause keeps AI off.
          pausedAiModeRef.current = newAiMode;
        } else {
          useSpaceStore.getState().setAiMode(newAiMode);
        }
        showNotification(
          AI_MODE_STUDENT_TOAST[newAiMode],
          NotificationType.SPACE_STUDENT,
        );
      },
    },
  );

  return {
    spaceInfo,
    patchSpaceInfo,
    joinComplete,
    writeMode,
    spaceClosed,
    closedFromStart,
    closedEditorSnapshot,
    spacePaused,
    savedCamera,
    sendCtrl,
  };
}
